/**
 * Chat hook with WebSocket transport and browser RPC support.
 *
 * Wraps useChat with WebSocket transport from context and provides
 * bidirectional RPC communication for browser tools.
 *
 * The WebSocket connection is managed at the app level via WebSocketProvider,
 * ensuring the connection survives component re-renders and page navigation.
 *
 * Updated to support multi-agent orchestrator (specs/021-multi-agent-orchestrator).
 */

import { useRef, useEffect, useMemo } from 'react';
import type { Message } from '../types';
import type { PlanningStrategy } from '../types/planning';
import type { TransportConnectionState } from '../transport/ChatTransport';
import { SSETransport } from '../transport/SSETransport';
import { getAuthToken } from '../context/AuthContext';
import { useChat } from './useChat';
import type { UseChatReturn, CanvasUpdateEvent, CanvasCompleteEvent, CanvasErrorEvent, ToolsAvailableEvent, SystemPromptEvent, SystemPromptInjectionsEvent } from './useChat';
import type { BrowserRpcMethods } from '../rpc/BrowserRpcMethods';
import { useWebSocket, useAgentOptional, useCxsMcpOptional } from '../context';

// Re-export event types for consumers
export type { CanvasUpdateEvent, CanvasCompleteEvent, CanvasErrorEvent, ToolsAvailableEvent, SystemPromptEvent, SystemPromptInjectionsEvent };

const ORCHESTRATOR_URL =
  process.env.REACT_APP_ORCHESTRATOR_URL ||
  process.env.REACT_APP_AGENT_URL ||
  'http://localhost:8090';

/** When true, POST SSE to /chat (single agent on REACT_APP_AGENT_URL). When false, orchestrator: coordinator uses POST /chat (delegation); other agents use POST /agents/{slug}/chat. */
const SINGLE_AGENT_SSE =
  process.env.REACT_APP_SINGLE_AGENT_SSE === 'true' ||
  process.env.REACT_APP_SINGLE_AGENT_SSE === '1';

export interface PlanStatusEvent {
  object: 'plan_status';
  event_type: 'plan_create' | 'plan_update' | 'plan_complete';
  strategy: PlanningStrategy;
  status: Record<string, unknown>;
}

export interface FileEvent {
  type: 'file-added' | 'file-deleted';
  data: {
    filename?: string;
    path: string;
    size?: number;
    mimeType?: string;
  };
}

export interface UseChatWithWebSocketOptions {
  sessionId: string | null;
  userId: string | null;
  organizationId?: string | null;
  onPlanStatusUpdate?: (event: PlanStatusEvent) => void;
  onFileEvent?: (event: FileEvent) => void;
  /** Canvas update callback (new/streaming canvas content) */
  onCanvasUpdate?: (event: CanvasUpdateEvent) => void;
  /** Canvas complete callback (finished rendering) */
  onCanvasComplete?: (event: CanvasCompleteEvent) => void;
  /** Canvas error callback (rendering failed) */
  onCanvasError?: (event: CanvasErrorEvent) => void;
  /** Callback when tools_available event received */
  onToolsAvailable?: (event: ToolsAvailableEvent) => void;
  /** Callback when system prompt inspection data received */
  onSystemPrompt?: (event: SystemPromptEvent) => void;
  /** Callback when system prompt injection data received */
  onSystemPromptInjections?: (event: SystemPromptInjectionsEvent) => void;
  initialMessages?: Message[];
  /** Use WebSocket transport (default: true) */
  useWebSocket?: boolean;
  /** Callback when WebSocket connection state changes */
  onConnectionStateChange?: (state: TransportConnectionState) => void;
  /** Extra fields merged into every chat request body (e.g. { slug: "my-ssp-agent" }) */
  extraBody?: Record<string, unknown>;
}

export interface UseChatWithWebSocketReturn extends UseChatReturn {
  /** WebSocket connection state */
  connectionState: TransportConnectionState;
  /** Whether WebSocket is currently active */
  isWebSocketActive: boolean;
  /** Browser tools instance for RPC */
  browserTools: BrowserRpcMethods | null;
}

/**
 * Chat hook with WebSocket transport and browser RPC support.
 *
 * Uses the session-scoped WebSocket connection from context.
 * Falls back to SSE if WebSocket is disabled or connection fails.
 */
export function useChatWithWebSocket(
  options: UseChatWithWebSocketOptions,
): UseChatWithWebSocketReturn {
  const {
    sessionId,
    userId,
    organizationId,
    onPlanStatusUpdate,
    onFileEvent,
    onCanvasUpdate,
    onCanvasComplete,
    onCanvasError,
    onToolsAvailable,
    onSystemPrompt,
    onSystemPromptInjections,
    initialMessages,
    useWebSocket: useWs = true,
    onConnectionStateChange,
    extraBody: extraBodyProp,
  } = options;

  // Get WebSocket connection from context (session-scoped singleton)
  const wsContext = useWebSocket();

  // Get selected agent from AgentContext (optional)
  const agentContext = useAgentOptional();

  // Get CXS MCP settings (optional — provider may not be mounted)
  const cxsMcp = useCxsMcpOptional();
  // URL slug for the SSE chat route. For Mystique this embeds the persona
  // (`mystique_{persona}`) instead of sending it in the request body.
  const selectedAgentSlug = agentContext?.routingSlug ?? undefined;
  /** Orchestrator default coordinator slug from GET /agents (usually dex). */
  const coordinatorSlug = agentContext?.defaultAgentSlug ?? 'dex';

  // SSE fallback transport ref
  const sseTransportRef = useRef<SSETransport | null>(null);
  const sseAgentSlugRef = useRef<string | null>(null);

  // Connect to WebSocket when sessionId/userId are available.
  // Depend on wsContext.connect (not full wsContext) to avoid re-triggering
  // on unrelated context changes like connectionState updates.
  const wsConnect = wsContext.connect;
  useEffect(() => {
    if (useWs && sessionId && userId) {
      wsConnect(sessionId, userId, organizationId ?? undefined).catch((error) => {
        console.warn('WebSocket connection failed:', error);
      });
    }
  }, [useWs, sessionId, userId, organizationId, wsConnect]);

  // Notify parent of connection state changes
  useEffect(() => {
    onConnectionStateChange?.(wsContext.connectionState);
  }, [wsContext.connectionState, onConnectionStateChange]);

  // Always use SSE for chat. WebSocket is for RPC (browser tools) only.
  const transport = useMemo(() => {
    const effectiveSlug = selectedAgentSlug || coordinatorSlug;
    // Orchestrator: POST /chat runs the coordinator with delegation (Dex → specialists).
    // POST /agents/{coordinator}/chat disables delegation — do not use for the default agent.
    const useCoordinatorEndpoint =
      SINGLE_AGENT_SSE ||
      effectiveSlug === coordinatorSlug;
    const routeKey = SINGLE_AGENT_SSE
      ? '__single__'
      : useCoordinatorEndpoint
        ? `__coord__:${coordinatorSlug}`
        : effectiveSlug;
    const chatEndpoint =
      SINGLE_AGENT_SSE || useCoordinatorEndpoint
        ? '/chat'
        : `/agents/${effectiveSlug}/chat`;

    // Recreate SSE transport if agent / routing mode changed
    if (sseTransportRef.current && sseAgentSlugRef.current !== routeKey) {
      sseTransportRef.current.dispose();
      sseTransportRef.current = null;
    }

    if (!sseTransportRef.current) {
      sseTransportRef.current = new SSETransport({
        baseUrl: ORCHESTRATOR_URL,
        chatEndpoint,
        headers: (): Record<string, string> => {
          const token = getAuthToken();
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
      });
      sseAgentSlugRef.current = routeKey;
    }
    return sseTransportRef.current;
  }, [selectedAgentSlug, coordinatorSlug]);

  // Cleanup SSE transport on unmount
  useEffect(() => {
    return () => {
      sseTransportRef.current?.dispose();
      sseTransportRef.current = null;
    };
  }, []);

  // CXS MCP settings are no longer sent in the request body.
  // The CXS2-minted JWT in the Authorization header carries all
  // needed context (accountId, userId, partition).
  const extraBody = extraBodyProp;

  // Use the main chat hook with our transport
  const chat = useChat({
    sessionId,
    userId,
    organizationId,
    transport,
    onPlanStatusUpdate,
    onFileEvent,
    onCanvasUpdate,
    onCanvasComplete,
    onCanvasError,
    onToolsAvailable,
    onSystemPrompt,
    onSystemPromptInjections,
    extraBody,
    initialMessages,
  });

  return {
    ...chat,
    connectionState: wsContext.connectionState,
    isWebSocketActive: wsContext.isConnected,
    browserTools: wsContext.browserTools,
  };
}
